import { observer } from 'mobx-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Col, Form, Modal, ToggleButton } from 'react-bootstrap';
import { makeBold } from '@common/utils';
import { FormInput } from '@common/components/forms';
import { PetCustomReminderModel } from '@common/models/PetCustomReminders';
import { Controller, useFormContext } from 'react-hook-form';
import { ReminderDays } from '@common/models/Enums';
import { DateTime } from 'luxon';
import { useApp } from '@common/hooks';
import { ReminderSourceModel } from '@common/models/ReminderSourceModel';

interface IThirdPartyRemindersDueDateModalProps {
    reminder: PetCustomReminderModel | undefined;
    onCancel: () => void;
}

interface IReminderDaysKeyValuePair {
    displayString: string;
    reminderDays: number;
}

interface IReminderSourceDateLabel {
    reminderSourceId: number;
    label: string;
}

export const ThirdPartyRemindersDueDateModal = observer(({ reminder, onCancel }: IThirdPartyRemindersDueDateModalProps) => {
    const { configuration } = useApp();
    const { control, setValue, reset, watch, getValues } = useFormContext<PetCustomReminderModel>();
    const dueDateValue = watch('dueDate');
    const dateGivenValue = watch('dateGiven');
    const reminderSourceIdValue = watch('reminderSourceId');
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedReminderSource, setSelectedReminderSource] = useState<IReminderSourceDateLabel | undefined>(undefined);
    const today = DateTime.utc().startOf('day');

    const previousDueDateRef = useRef<string>('');
    const previousDateGivenRef = useRef<string>('');
    const previousReminderSourceIdRef = useRef<number>(0);
    const previousReminderNameRef = useRef<string>('');


    const getDatePlusReminderDays = useCallback((date: string, forGivenDate: boolean = true) => {
        const dateTime = DateTime.fromISO(date)
        return forGivenDate
            ? dateTime.minus({ days: reminder!.reminderDays }).toISODate()
            : dateTime.plus({ days: reminder!.reminderDays }).toISODate();
    }, [reminder]);

    useEffect(() => {
        setSelectedDate(dueDateValue ? dueDateValue : '');
        if (selectedReminderSource?.reminderSourceId === 1 && dueDateValue && previousDueDateRef.current !== dueDateValue && reminder?.reminderDays) {
            const addedGivenDate = getDatePlusReminderDays(dueDateValue);
            if (addedGivenDate !== previousDueDateRef.current) {
                setValue("dateGiven", addedGivenDate, { shouldDirty: true });
            }
        }
    }, [dueDateValue, getDatePlusReminderDays, reminder?.reminderDays, selectedReminderSource?.reminderSourceId, setValue]);

    useEffect(() => {
        if (selectedReminderSource?.reminderSourceId === 1 && dateGivenValue && previousDateGivenRef.current !== dateGivenValue && reminder?.reminderDays) {
            const addedDueDate = getDatePlusReminderDays(dateGivenValue, false);
            if (addedDueDate !== previousDateGivenRef.current) {
                setValue("dueDate", addedDueDate, { shouldDirty: true });
            }
        }
    }, [dateGivenValue, getDatePlusReminderDays, reminder?.reminderDays, selectedReminderSource?.reminderSourceId, setValue]);

    const getDateLabel = (reminderSourceId: number) => reminderSourceId === 1 ? 'Date Given' : 'Date Recommended';

    useEffect(() => {
        if (!reminder) return;

        if (reminder.dueDate) {
            setSelectedDate(reminder.dueDate);
            setValue("dueDate", reminder.dueDate);
            previousDueDateRef.current = reminder.dueDate;
        } else {
            reset();
        }

        if (reminder.dateGiven) {
            setValue("dateGiven", reminder.dateGiven);
            previousDateGivenRef.current = reminder.dateGiven;
        }

        if (reminder.reminderSourceId) {
            setValue("reminderSourceId", reminder.reminderSourceId);
            setSelectedReminderSource({ reminderSourceId: reminder.reminderSourceId, label: getDateLabel(reminder.reminderSourceId) });
            previousReminderSourceIdRef.current = reminder.reminderSourceId;
            previousReminderNameRef.current = reminder.reminderSourceName;
        }
    }, [reminder, setValue, reset]);

    useEffect(() => {
        let valueToSet = '';
        if (!selectedReminderSource) return;
        if (selectedReminderSource?.reminderSourceId === 1) {
            valueToSet = previousDateGivenRef.current && previousDateGivenRef.current !== reminder!.dateGiven
                ? previousDateGivenRef.current
                : reminder!.dateGiven;
        }
        else if (selectedReminderSource?.reminderSourceId === 2) {
            valueToSet = today.toISODate();
        }
        else {
            valueToSet = '';
        }
        if (previousDateGivenRef.current >= dueDateValue) {
            setValue("dueDate", '');
        }
        reminder!.dateGiven = valueToSet;
        setValue("dateGiven", valueToSet);
        //using the next eslint disable due to today const causing multiple executions of this effect
        //and not even allowing the logic to execute
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reminder, selectedReminderSource?.reminderSourceId, setValue]);

    const resetReminder = () => {
        reminder!.dueDate = '';
        reminder!.dateGiven = '';
        reminder!.reminderSourceId = 0;
        setSelectedDate('');
    };

    const restorePreviousReminder = () => {
        reminder!.dueDate = previousDueDateRef.current;
        reminder!.dateGiven = previousDateGivenRef.current;
        reminder!.reminderSourceId = previousReminderSourceIdRef.current;
        reminder!.reminderSourceName = previousReminderNameRef.current;
    };

    const onContinue = () => {
        reminder!.dueDate = getValues("dueDate");
        reminder!.dateGiven = getValues("dateGiven");
        reminder!.reminded = true;
        onCancel();
    };

    const closeModal = () => {
        if (!reminder?.reminded) {
            resetReminder();
        } else {
            restorePreviousReminder();
        }
        reset();
        onCancel();
    };

    const getDateValue = useCallback((days: number) => today.plus({ days: days }).toISODate(), [today]);

    const onSelectDateFromButton = useCallback((days: number) => {
        const valueToSet = getDateValue(days);
        setSelectedDate(valueToSet);
        setValue("dueDate", valueToSet, { shouldDirty: true });
        reminder!.dueDate = valueToSet;
    }, [getDateValue, reminder, setValue]);

    const reminderOptions: IReminderDaysKeyValuePair[] = [
        {
            displayString: "1 Month(s)",
            reminderDays: ReminderDays.One_Month
        },
        {
            displayString: "3 Month(s)",
            reminderDays: ReminderDays.Three_Months
        },
        {
            displayString: "6 Month(s)",
            reminderDays: ReminderDays.Six_Months
        },
        {
            displayString: "1 Year",
            reminderDays: ReminderDays.Year
        },
    ];

    const getMinDate = (): Date => {
        const dateGiven = getValues("dateGiven");
        return DateTime.fromISO(dateGiven).plus({ days: 1 }).toJSDate();
    }

    return (
        <Modal centered size='lg' show={true} backdrop="static" onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>{makeBold(reminder?.salesItemName, undefined)}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <section className='mt-2'>
                    {
                        // Decided not to use a selectable property or something similar 
                        // because the checked property validation can be done 
                        // using the reminder object passed into this component
                        // and onChange always updates the reminderSourceId
                        // which is what's needed for comparison
                        configuration.reminderSources.map((rs: ReminderSourceModel, index: number) =>
                            <Controller
                                name={`reminderSourceId`}
                                control={control}
                                key={`reminderSourceId_${index}`}
                                render={({ field: { onChange, name } }) => {
                                    return <Form.Check
                                        label={rs.name}
                                        type="radio"
                                        name={name}
                                        data-testid={`reminderSourceId_${index}`}
                                        className='third-party-reminder-checkbox'
                                        checked={reminder!.reminderSourceId == rs.reminderSourceId}
                                        value={rs.reminderSourceId}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setValue("reminderSourceId", rs.reminderSourceId);
                                                setSelectedReminderSource({
                                                    reminderSourceId: rs.reminderSourceId,
                                                    label: getDateLabel(rs.reminderSourceId)
                                                } as IReminderSourceDateLabel);
                                                reminder!.reminderSourceId = rs.reminderSourceId;
                                                reminder!.reminderSourceName = rs.name;
                                                onChange(e);
                                            }
                                        }} />
                                }}
                            />
                        )
                    }
                </section>
                {
                    selectedReminderSource === undefined
                        ? undefined
                        : <>
                            <Col className='pb-2'>
                                <FormInput
                                    type='date'
                                    label={`${selectedReminderSource.label}:`}
                                    fieldName='dateGiven'
                                    control={control}
                                    portalId="root-portal"
                                    maxDate={selectedReminderSource.reminderSourceId == 1 ? DateTime.utc().toJSDate() : undefined}
                                    readOnly={selectedReminderSource.reminderSourceId === 2}
                                    isClearable={selectedReminderSource.reminderSourceId === 1}
                                />
                            </Col>
                            <Col className='pb-2'>
                                <FormInput
                                    type='date'
                                    label='Due Date:'
                                    fieldName='dueDate'
                                    control={control}
                                    portalId="root-portal"
                                    minDate={getMinDate()}
                                />
                            </Col>
                        </>
                }
                {
                    selectedReminderSource?.reminderSourceId === 2 &&
                    <section className='d-flex gap-1 pt-3'>
                        {
                            reminderOptions.map(reminderOption => (
                                <ToggleButton
                                    key={reminderOption.reminderDays}
                                    variant='outline-primary'
                                    onClick={() => onSelectDateFromButton(reminderOption.reminderDays)}
                                    active={selectedDate === getDateValue(reminderOption.reminderDays)}
                                    value={getDateValue(reminderOption.reminderDays)}
                                >
                                    {reminderOption.displayString}
                                </ToggleButton>
                            ))
                        }
                    </section>
                }
            </Modal.Body>
            <Modal.Footer>
                <Button variant='primary' disabled={!dueDateValue || !dateGivenValue || !reminderSourceIdValue} onClick={onContinue}>Continue</Button>
            </Modal.Footer>
        </Modal>
    )
});
